import { OTHER_MEETINGS_BUCKET_ID } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { Center, Group, Paper, Stack, Text } from "@mantine/core"
import { useState } from "react"
import { formatInteger } from "../../../../lib/format-date"
import { categoryTone, splitTopMeetings, toPercent, visibleCategories } from "../credits-helpers"
import { creditMeetingTitle } from "../meeting-label"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/** How many meetings get their own bar before the rest fold into one row. */
const TOP_MEETINGS = 5

/**
 * The metering `action` tag for Ask-AI spend (the backend's `meeting_qa`). It
 * folds into the `llm` category on the stacked bar, so we surface it as its own
 * detail line — credits + how many questions produced them.
 */
const ASK_AI_ACTION = "meeting_qa"

/** Friendly, translated labels for the usage categories the ledger tags. */
function categoryLabel(i18n: ReturnType<typeof useLingui>["i18n"], category: string): string {
  switch (category) {
    case "transcription":
      return i18n._(msg`Transcription`)
    case "llm":
      return i18n._(msg`Summaries & analysis`)
    case "image":
      return i18n._(msg`Images`)
    case "embedding":
      return i18n._(msg`Search index`)
    default:
      return category
  }
}

/**
 * "Where your credits went" — a credits-only spend breakdown. A single stacked
 * bar splits the period's spend by category (with a legend), and a ranked bar
 * list shows the heaviest meetings with the long tail folded into one row. Pure
 * presentation; the smart wrapper owns the window. CREDITS ONLY.
 */
export const CreditStatementView = ({ statement }: IProps): React.JSX.Element => {
  const { i18n } = useLingui()
  const [expanded, setExpanded] = useState<boolean>(false)

  const isEmpty = !statement || statement.totalCredits === 0
  const categories = visibleCategories(statement?.byCategory ?? [])
  const askAi = statement?.byAction.find((row) => row.action === ASK_AI_ACTION) ?? null
  // The backend hands a restricted member ONE aggregate row for every meeting
  // they may not see individually. It names nothing, so it stays out of the
  // ranked list and renders as its own "Other meetings" line.
  const meetingRows = statement?.byMeeting ?? []
  const otherRow = meetingRows.find((row) => row.meetingId === OTHER_MEETINGS_BUCKET_ID) ?? null
  const namedRows = otherRow ? meetingRows.filter((row) => row.meetingId !== OTHER_MEETINGS_BUCKET_ID) : meetingRows
  const { top, rest, restCredits, restCount } = splitTopMeetings(namedRows, TOP_MEETINGS)
  const shown = expanded ? [...top, ...rest] : top
  const maxMeetingCredits = top[0]?.credits ?? 0

  return (
    <Paper withBorder radius="md" p="lg">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        <Trans>Where your credits went</Trans>
      </Text>

      {isEmpty ? (
        <Center py="xl">
          <Text c="dimmed" size="sm">
            <Trans>No credit spend in this period</Trans>
          </Text>
        </Center>
      ) : (
        <>
          <div className={classes.stackbar} aria-hidden>
            {categories.map((row) => (
              <span
                key={row.category}
                className={classes.segment}
                data-tone={categoryTone(row.category)}
                style={{ width: `${toPercent(row.credits, statement.totalCredits)}%` }}
              />
            ))}
          </div>

          <Group gap="md" mt="sm" className={classes.legend}>
            {categories.map((row) => (
              <Group key={row.category} gap={6} align="center" wrap="nowrap">
                <span className={classes.swatch} data-tone={categoryTone(row.category)} aria-hidden />
                <Text size="sm" c="dimmed">
                  {categoryLabel(i18n, row.category)}
                </Text>
                <Text size="sm" fw={700} className={classes.num}>
                  {formatInteger(i18n, row.credits)}
                </Text>
              </Group>
            ))}
          </Group>

          {askAi ? (
            <Group gap={8} align="baseline" className={classes.askAi} wrap="nowrap">
              <Text size="sm" c="dimmed">
                <Trans>Ask AI</Trans>
              </Text>
              <Text size="sm" fw={700} className={classes.num}>
                {formatInteger(i18n, askAi.credits)}
              </Text>
              <Text size="xs" c="dimmed">
                <Trans>from {formatInteger(i18n, askAi.callCount)} questions</Trans>
              </Text>
            </Group>
          ) : null}

          <Stack gap={0} mt="lg">
            {shown.map((row) => (
              <div key={row.meetingId || "unattributed"} className={classes.meetingRow}>
                <Text size="sm" truncate className={classes.meetingName}>
                  {creditMeetingTitle(i18n, row)}
                </Text>
                <span className={classes.meetingBar} aria-hidden>
                  <span
                    className={classes.meetingFill}
                    style={{ width: `${toPercent(row.credits, maxMeetingCredits)}%` }}
                  />
                </span>
                <Text size="sm" fw={700} ta="right" className={classes.num}>
                  {formatInteger(i18n, row.credits)}
                </Text>
              </div>
            ))}

            {restCount > 0 ? (
              <button
                type="button"
                className={`${classes.meetingRow} ${classes.moreButton}`}
                aria-expanded={expanded}
                onClick={() => setExpanded((open) => !open)}
              >
                <Text size="sm" c="dimmed">
                  {expanded ? (
                    <Trans>Show less</Trans>
                  ) : (
                    <Plural value={restCount} one="# more meeting…" other="# more meetings…" />
                  )}
                </Text>
                <span aria-hidden />
                <Text size="sm" fw={700} ta="right" c="dimmed" className={classes.num}>
                  {expanded ? "" : formatInteger(i18n, restCredits)}
                </Text>
              </button>
            ) : null}

            {otherRow ? (
              <div className={classes.meetingRow} data-muted>
                <Text size="sm" c="dimmed">
                  <Trans>Other meetings</Trans>
                </Text>
                <span aria-hidden />
                <Text size="sm" fw={700} ta="right" c="dimmed" className={classes.num}>
                  {formatInteger(i18n, otherRow.credits)}
                </Text>
              </div>
            ) : null}
          </Stack>
        </>
      )}
    </Paper>
  )
}
